import { useState, useEffect } from "react";
import PropTypes from "prop-types";
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
  CircularProgress,
  InputLabel,
  TextField,
  MenuItem,
  FormControl,
  Select,
  Button,
} from "@mui/material";
import {
  AdminPanelSettings,
  ArrowBack,
  Attachment,
  CalendarMonth,
  CalendarViewDay,
  Delete,
  Discount,
  Money,
  Paid,
  Payment,
  Payments,
  Savings,
} from "@mui/icons-material";
import {
  getStudentApplicableFees,
  getPaymentFrequencies,
  addStudentFee,
  getStudentFees,
  deleteFee,
} from "../../../../ApiService/ApiService";
import { calculateTotalAmount } from "../../../../Utils/calculateTotalAmount";
import "../../../../Styles/dashboard.css";
import CustomDialog from "../../../../Components/CustomDialog";

const headCells = [
  {
    id: "feeType",
    label: "Fee Type",
    icon: <Payment sx={{ marginRight: 1 }} />,
  },
  {
    id: "amount",
    label: "Amount",
    icon: <Money sx={{ marginRight: 1 }} />,
  },
  {
    id: "startDate",
    label: "Start Date",
    icon: <CalendarMonth sx={{ marginRight: 1 }} />,
  },
  {
    id: "endDate",
    label: "End Date",
    icon: <CalendarMonth sx={{ marginRight: 1 }} />,
  },
  {
    id: "applicableFrom",
    label: "Applicable From",
    icon: <CalendarViewDay sx={{ marginRight: 1 }} />,
  },
  {
    id: "discountName",
    label: "Discount Name",
    icon: <Savings sx={{ marginRight: 1 }} />,
  },
  {
    id: "discount",
    label: "% Discount",
    icon: <Discount sx={{ marginRight: 1 }} />,
  },
  {
    id: "paymentFrequency",
    label: "Payment Frequency",
    icon: <Payments sx={{ marginRight: 1 }} />,
  },

  {
    id: "finalAmount",
    label: "Final Amount",
    icon: <Paid sx={{ marginRight: 1 }} />,
  },
  {
    id: "actions",
    label: "Actions",
    icon: <AdminPanelSettings sx={{ marginRight: 1 }} />,
  },
];

function AttachFees({ setActionComponent, selectedStudentId }) {
  const schoolId = parseInt(sessionStorage.getItem("schoolId"));

  const [studentFees, setStudentFees] = useState([]);

  const [applicableFees, setApplicableFees] = useState({});
  const [paymentFrequencyData, setPaymentFrequencyData] = useState([]);
  const [selectedPayFrequencyId, setSelectedPayFrequencyId] = useState(1);
  const [discountValues, setDiscountValues] = useState([]);

  const [paginatedData, setPaginatedData] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);

  const [loading, setLoading] = useState(false);
  const [dialog, setDialog] = useState({
    visible: false,
    title: "",
    message: "",
    buttons: [],
  });
  const handleDialogClose = () => {
    setDialog({ ...dialog, visible: false });
  };

  const fetchStudentFees = async () => {
    try {
      setLoading(true);
      const response = await getStudentFees(selectedStudentId);
      //   console.log(response?.data);
      setStudentFees(response?.data?.data);
    } catch (error) {
      console.log("Error fetching students fees:", error);
    } finally {
      setLoading(false);
    }
  };

  console.log("STUDENTS FEE", studentFees);

  const fetchApplicableFees = async () => {
    try {
      setLoading(true);
      const response = await getStudentApplicableFees(selectedStudentId);
      setApplicableFees(response?.data);
    } catch (error) {
      console.log("Error fetching students applicable fees:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchPaymentFrequencies = async () => {
    try {
      const response = await getPaymentFrequencies();
      setPaymentFrequencyData(response?.data);
    } catch (error) {
      console.log("Error fetching payment frequencies:", error);
    }
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  useEffect(() => {
    fetchStudentFees();
    fetchApplicableFees();
    fetchPaymentFrequencies();
  }, [selectedStudentId]);

  useEffect(() => {
    if (applicableFees?.data) {
      const defaultData = applicableFees?.data?.map((fee) => ({
        ...fee,
        discountName: "No Discount",
        discount: 0,
        paymentFrequencyId: fee?.feeType?.isOneTime ? "One Time" : 1,
      }));
      setPaginatedData(defaultData);
      setDiscountValues(
        defaultData.map(() => ({
          discountName: "No Discount",
          discount: 0,
          installments: 1,
        }))
      );
    }
  }, [applicableFees]);

  const handleDiscountNameChange = (event, index) => {
    const newData = [...paginatedData];
    newData[index] = {
      ...newData[index],
      discountName: event.target.value,
    };
    setPaginatedData(newData);
    setDiscountValues((prevState) => ({
      ...prevState,
      [index]: {
        ...prevState[index],
        discountName: newData[index].discountName,
      },
    }));
  };

  const handlePaymentFrequencyChange = (event, index) => {
    const newData = [...paginatedData];
    const selectedFrequencyId = event.target.value;
    setSelectedPayFrequencyId(selectedFrequencyId);
    const selectedFrequency = paymentFrequencyData?.data?.find(
      (item) => item.id === selectedFrequencyId
    );
    console.log(selectedFrequency);
    const newInstallments = selectedFrequency?.installmentPerFrequency;

    newData[index].paymentFrequencyId = selectedFrequency?.id;
    setDiscountValues((prevState) => ({
      ...prevState,
      [index]: {
        ...prevState[index],
        installments: newInstallments,
      },
    }));
    setPaginatedData(newData);
  };

  const handleDiscountChange = (event, index) => {
    const newData = [...paginatedData];
    newData[index] = {
      ...newData[index],
      discount: event.target.value,
    };
    setDiscountValues((prevState) => ({
      ...prevState,
      [index]: {
        ...prevState[index],
        discount: newData[index].discount,
      },
    }));
    setPaginatedData(newData);
  };

  const handleStartDateChange = (event, index) => {
    const newData = [...paginatedData];
    newData[index] = {
      ...newData[index],
      startDate: event.target.value,
    };
    setPaginatedData(newData);
  };

  const handleEndDateChange = (event, index) => {
    const newData = [...paginatedData];
    newData[index] = {
      ...newData[index],
      endDate: event.target.value,
    };
    setPaginatedData(newData);
  };

  const handleChangeApplicableFrom = (event, index) => {
    const newData = [...paginatedData];
    newData[index] = {
      ...newData[index],
      applicableFrom: event.target.value,
    };
    setPaginatedData(newData);
  };

  const updatePaginatedData = (paginatedData) => {
    const discounts = {};
    paginatedData?.forEach((row, index) => {
      if (row.discount) {
        discounts[index] = {
          name: row.discountName,
          discount: row.discount,
          installments: discountValues[index]?.installments,
        };
      }
    });
  };

  useEffect(() => {
    updatePaginatedData(paginatedData);
  }, [paginatedData]);

  const handleAddFee = async (row, index) => {
    try {
      const isDefaultDiscount =
        row.discountName === "No Discount" && row.discount === 0;
      console.log("Selected Id", selectedPayFrequencyId);
      const data = {
        feeTypeId: row.feeType.id,
        schoolId: schoolId,
        studentId: selectedStudentId,
        schoolFeeId: row.id,
        startDate: row.startDate,
        endDate: row.endDate,
        applicableFrom: row.applicableFrom,
        paymentFrequencyId: row?.feeType?.isOneTime
          ? null
          : selectedPayFrequencyId,
        discountName: isDefaultDiscount ? "No Discount" : row.discountName,
        discountPercentage: isDefaultDiscount ? 0 : parseFloat(row.discount),
        finalAmount: calculateTotalAmount(
          row.amount,
          discountValues[index].installments,
          discountValues[index].discount
        ),
      };

      // console.log("DATA", data);
      const response = await addStudentFee(data);
      // console.log("RES", response?.data);
      if (response?.data?.status === 200) {
        setDialog({
          visible: true,
          title: "Success",
          message: "Fee added successfully",
          buttons: [{ name: "OK", onPress: handleDialogClose }],
        });
        fetchStudentFees();
      } else {
        const errorMessage =
          response?.data?.errorMessage || "Something went wrong";
        setDialog({
          visible: true,
          title: "Error",
          message: errorMessage,
          buttons: [{ name: "Close", onPress: handleDialogClose }],
        });
      }
    } catch (error) {
      setDialog({
        visible: true,
        title: "Error",
        message: "Something went wrong",
        buttons: [{ name: "Close", onPress: handleDialogClose }],
      });
    }
  };

  const handleDeleteFee = async (feeIdToDelete) => {
    try {
      const response = await deleteFee(selectedStudentId, feeIdToDelete);
      if (response?.data?.status === 200) {
        setDialog({
          visible: true,
          title: "Success",
          message: "Fee deleted successfully",
          buttons: [{ name: "OK", onPress: handleDialogClose }],
        });
        fetchStudentFees();
      } else {
        const errorMessage =
          response?.data?.errorMessage || "Something went wrong";
        setDialog({
          visible: true,
          title: "Error",
          message: errorMessage,
          buttons: [{ name: "Close", onPress: handleDialogClose }],
        });
      }
    } catch (error) {
      setDialog({
        visible: true,
        title: "Error",
        message: "Something went wrong",
        buttons: [{ name: "Close", onPress: handleDialogClose }],
      });
    }
  };

  const handleFeeAction = async (row, index) => {
    const isFeeAttached = !!studentFees.find(
      (fee) => fee?.schoolFee?.feeType?.id === row?.feeType?.id
    );

    if (isFeeAttached) {
      const feeIdToDelete = studentFees.find(
        (fee) => fee?.schoolFee?.feeType?.id === row?.feeType?.id
      )?.id;
      setDialog({
        visible: true,
        title: "Confirm Deletion",
        message:
          "Delete Fee will delete all the paid fees for this student under this fee type. Do you want to proceed?",
        buttons: [
          { name: "No", onPress: handleDialogClose },
          {
            name: "Yes",
            onPress: () => {
              handleDeleteFee(feeIdToDelete);
              handleDialogClose();
            },
          },
        ],
      });
    } else {
      await handleAddFee(row, index);
    }
  };

  const slicedData = paginatedData?.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  return (
    <>
      <CustomDialog
        visible={dialog.visible}
        title={dialog.title}
        message={dialog.message}
        buttons={dialog.buttons}
        onClose={handleDialogClose}
      />
      <Box>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Typography
            paragraph
            sx={{
              color: "black",
              fontWeight: 600,
              fontSize: 22,
            }}
            xs={4}
            sm={4}
            md={6}
            lg={8}
            xl={8}
          >
            Attach Fees
          </Typography>
          <Button
            variant="contained"
            color="inherit"
            size="small"
            sx={{ textTransform: "unset", borderRadius: "10px", width: "10%" }}
            onClick={() => setActionComponent(null)}
          >
            <ArrowBack fontSize="small" sx={{ marginRight: 1 }} />
            Back
          </Button>
        </Box>

        <Paper>
          <TableContainer>
            <Table aria-labelledby="tableTitle" size="small">
              <TableHead>
                <TableRow>
                  {headCells.map((headCell) => (
                    <TableCell key={headCell.id}>
                      <Box className="table-head">
                        {headCell.icon}
                        {headCell.label}
                      </Box>
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>

              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center">
                      <CircularProgress />
                    </TableCell>
                  </TableRow>
                ) : slicedData?.length > 0 ? (
                  slicedData.map((row, index) => (
                    <TableRow key={row?.id}>
                      <TableCell
                        component="th"
                        scope="row"
                        className="table-cell"
                      >
                        {row?.feeType?.name}
                      </TableCell>
                      <TableCell className="table-cell">
                        {row?.amount?.toFixed(2)}
                      </TableCell>
                      <TableCell size="small">
                        <TextField
                          fullWidth
                          name="startDate"
                          type="date"
                          labelid="Start Date"
                          value={
                            studentFees?.find(
                              (fee) =>
                                fee?.schoolFee?.feeType?.id === row?.feeType?.id
                            )?.startDate || row.startDate
                          }
                          onChange={(event) =>
                            handleStartDateChange(event, index)
                          }
                          disabled={studentFees?.find(
                            (fee) =>
                              fee?.schoolFee?.feeType?.id === row?.feeType?.id
                          )}
                          size="small"
                          required
                        />
                      </TableCell>
                      <TableCell size="small">
                        <TextField
                          fullWidth
                          name="endDate"
                          type="date"
                          labelid="End Date"
                          value={
                            studentFees?.find(
                              (fee) =>
                                fee?.schoolFee?.feeType?.id === row?.feeType?.id
                            )?.endDate || row.endDate
                          }
                          onChange={(event) =>
                            handleEndDateChange(event, index)
                          }
                          disabled={studentFees?.find(
                            (fee) =>
                              fee?.schoolFee?.feeType?.id === row?.feeType?.id
                          )}
                          size="small"
                          required
                        />
                      </TableCell>
                      <TableCell size="small">
                        <TextField
                          fullWidth
                          name="applicableFrom"
                          type="date"
                          labelid="Applicable From"
                          value={
                            studentFees?.find(
                              (fee) =>
                                fee?.schoolFee?.feeType?.id === row?.feeType?.id
                            )?.applicableFrom || row.applicaleFrom
                          }
                          onChange={(event) =>
                            handleChangeApplicableFrom(event, index)
                          }
                          disabled={studentFees?.find(
                            (fee) =>
                              fee?.schoolFee?.feeType?.id === row?.feeType?.id
                          )}
                          size="small"
                          required
                        />
                      </TableCell>
                      <TableCell>
                        <TextField
                          fullWidth
                          label="Discount Name"
                          name="discountName"
                          type="text"
                          size="small"
                          required
                          value={
                            studentFees?.find(
                              (fee) =>
                                fee?.schoolFee?.feeType?.id === row?.feeType?.id
                            )?.discountName || row.discountName
                          }
                          onChange={(event) =>
                            handleDiscountNameChange(event, index)
                          }
                          disabled={studentFees?.find(
                            (fee) =>
                              fee?.schoolFee?.feeType?.id === row?.feeType?.id
                          )}
                        />
                      </TableCell>
                      <TableCell>
                        <TextField
                          fullWidth
                          label="% Discount"
                          name="discount"
                          type="number"
                          size="small"
                          required
                          value={
                            studentFees?.find(
                              (fee) =>
                                fee?.schoolFee?.feeType?.id === row?.feeType?.id
                            )?.discountPercentage || row?.discount
                          }
                          onChange={(event) =>
                            handleDiscountChange(event, index)
                          }
                          inputProps={{ step: "any", min: 0 }}
                          disabled={
                            !!studentFees?.find(
                              (fee) =>
                                fee?.schoolFee?.feeType?.id === row?.feeType?.id
                            )
                          }
                        />
                      </TableCell>

                      <TableCell>
                        <FormControl fullWidth size="small" required>
                          <InputLabel id="paymentFreq">Frequency</InputLabel>
                          <Select
                            required
                            label="Frequency"
                            labelId="paymentFreq"
                            value={
                              studentFees?.find(
                                (fee) =>
                                  fee?.schoolFee?.feeType?.id ===
                                  row?.feeType?.id
                              )?.paymentFrequencyDto?.id ||
                              row?.paymentFrequencyId
                            }
                            disabled={
                              !!studentFees?.find(
                                (fee) =>
                                  fee?.schoolFee?.feeType?.id ===
                                  row?.feeType?.id
                              ) || row?.feeType?.isOneTime
                            }
                            onChange={(event) =>
                              handlePaymentFrequencyChange(event, index)
                            }
                          >
                            {paymentFrequencyData?.data?.map((item) => (
                              <MenuItem key={item?.id} value={item?.id}>
                                {item?.name}
                              </MenuItem>
                            ))}
                            {row?.feeType?.isOneTime && (
                              <MenuItem value="One Time">One Time</MenuItem>
                            )}
                          </Select>
                        </FormControl>
                      </TableCell>

                      <TableCell>
                        {studentFees
                          ?.find(
                            (fee) =>
                              fee?.schoolFee?.feeType?.id === row?.feeType?.id
                          )
                          ?.finalAmount.toFixed(2) ||
                          calculateTotalAmount(
                            row.amount,
                            discountValues[index].installments,
                            discountValues[index].discount
                          ).toFixed(2)}
                      </TableCell>

                      <TableCell>
                        <Button
                          variant="contained"
                          size="small"
                          startIcon={
                            studentFees?.find(
                              (fee) =>
                                fee?.schoolFee?.feeType?.id === row?.feeType?.id
                            ) ? (
                              <Delete />
                            ) : (
                              <Attachment />
                            )
                          }
                          onClick={() => handleFeeAction(row, index)}
                          sx={{ textTransform: "unset", borderRadius: 3 }}
                          color={
                            studentFees?.find(
                              (fee) =>
                                fee?.schoolFee?.feeType?.id === row?.feeType?.id
                            )
                              ? "error"
                              : "primary"
                          }
                        >
                          {studentFees?.find(
                            (fee) =>
                              fee?.schoolFee?.feeType?.id === row?.feeType?.id
                          )
                            ? "Delete"
                            : "Attach"}
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={2}>
                      <Typography variant="body2" color="textSecondary">
                        No data available
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {slicedData?.length > 0 && (
            <TablePagination
              rowsPerPageOptions={[10]}
              component="div"
              count={paginatedData?.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          )}
        </Paper>
      </Box>
    </>
  );
}

AttachFees.propTypes = {
  setActionComponent: PropTypes.func.isRequired,
  selectedStudentId: PropTypes.number.isRequired,
};

export default AttachFees;
